import type { BufferAttribute, Object3D, SkinnedMesh } from 'three';

import {
  KEY_POSE_FK_JOINT_CONFIG_BY_ID,
  KEY_POSE_FK_JOINT_IDS,
} from '../fk/keyPoseFkConfig';
import type { KeyPoseFkJointId } from '../types';
import type { MotionEditorBonePickerSkeletonType } from './types';

const JOINT_ID_BY_BONE_NAME = new Map<string, KeyPoseFkJointId>();

for (const jointId of KEY_POSE_FK_JOINT_IDS) {
  const config = KEY_POSE_FK_JOINT_CONFIG_BY_ID[jointId];
  JOINT_ID_BY_BONE_NAME.set(normalizeBoneName(jointId), jointId);
  for (const boneName of config.boneNames) {
    JOINT_ID_BY_BONE_NAME.set(normalizeBoneName(boneName), jointId);
  }
}

interface ResolvedBoneWeight {
  jointId: KeyPoseFkJointId;
  sourceBoneName: string;
  sourceWeight: number;
  weight: number;
}

export interface PoseMeshPickMeta {
  jointId: KeyPoseFkJointId;
  sourceBoneName: string;
}

export function resolvePoseMeshBoneJointId(
  bone: Object3D | null | undefined,
  skeletonType: MotionEditorBonePickerSkeletonType,
) {
  if (!bone) return null;

  const candidates = [
    bone.name,
    getUserDataString(bone, 'autoAssign'),
    getUserDataString(bone, 'originalName'),
  ].filter((value): value is string => Boolean(value));

  for (const candidate of candidates) {
    const directJointId = JOINT_ID_BY_BONE_NAME.get(
      normalizeBoneName(candidate),
    );
    if (directJointId) return directJointId;
  }

  for (const candidate of candidates) {
    const detailJointId = resolveDetailBoneName(candidate, skeletonType);
    if (detailJointId) return detailJointId;
  }

  return null;
}

export function buildPoseMeshVertexGroups(
  mesh: SkinnedMesh,
  skeletonType: MotionEditorBonePickerSkeletonType,
) {
  const geometry = mesh.geometry;
  const position = geometry.getAttribute('position') as BufferAttribute;
  const skinIndex = geometry.getAttribute('skinIndex') as
    | BufferAttribute
    | undefined;
  const skinWeight = geometry.getAttribute('skinWeight') as
    | BufferAttribute
    | undefined;
  const bones = mesh.skeleton?.bones;
  if (!position || !skinIndex || !skinWeight || !bones?.length) {
    return new Map<KeyPoseFkJointId, number[]>();
  }

  const vertexGroups = new Map<KeyPoseFkJointId, number[]>();
  const vertexCount = Math.min(
    position.count,
    skinIndex.count,
    skinWeight.count,
  );
  const itemSize = Math.min(skinIndex.itemSize, skinWeight.itemSize, 4);

  for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex += 1) {
    const best = resolveDominantJointForVertex({
      bones,
      itemSize,
      skeletonType,
      skinIndex,
      skinWeight,
      vertexIndex,
    });
    if (!best) continue;

    const vertices = vertexGroups.get(best.jointId) ?? [];
    vertices.push(vertexIndex);
    vertexGroups.set(best.jointId, vertices);
  }

  return vertexGroups;
}

export function posePickMetaFromSkinWeights({
  barycentricWeights,
  mesh,
  skeletonType,
  vertexIndices,
}: {
  barycentricWeights: readonly [number, number, number];
  mesh: SkinnedMesh;
  skeletonType: MotionEditorBonePickerSkeletonType;
  vertexIndices: readonly [number, number, number];
}): PoseMeshPickMeta | null {
  const geometry = mesh.geometry;
  const skinIndex = geometry.getAttribute('skinIndex') as
    | BufferAttribute
    | undefined;
  const skinWeight = geometry.getAttribute('skinWeight') as
    | BufferAttribute
    | undefined;
  const bones = mesh.skeleton?.bones;
  if (!skinIndex || !skinWeight || !bones?.length) return null;

  const weightsByJoint = new Map<KeyPoseFkJointId, ResolvedBoneWeight>();
  const itemSize = Math.min(skinIndex.itemSize, skinWeight.itemSize, 4);

  for (let vertexSlot = 0; vertexSlot < 3; vertexSlot += 1) {
    const vertexIndex = vertexIndices[vertexSlot];
    const barycentricWeight = Number(barycentricWeights[vertexSlot] || 0);
    if (barycentricWeight <= 1e-6) continue;

    for (let component = 0; component < itemSize; component += 1) {
      const weight =
        barycentricWeight *
        Number(bufferAttributeComponent(skinWeight, vertexIndex, component));
      if (weight <= 1e-6) continue;

      const boneIndex = Math.round(
        Number(bufferAttributeComponent(skinIndex, vertexIndex, component)),
      );
      const bone = bones[boneIndex];
      const jointId = resolvePoseMeshBoneJointId(bone, skeletonType);
      if (!jointId) continue;

      const current = weightsByJoint.get(jointId) ?? {
        jointId,
        sourceBoneName: bone?.name || jointId,
        sourceWeight: 0,
        weight: 0,
      };
      current.weight += weight;
      if (weight > current.sourceWeight) {
        current.sourceBoneName = bone?.name || jointId;
        current.sourceWeight = weight;
      }
      weightsByJoint.set(jointId, current);
    }
  }

  let best: ResolvedBoneWeight | null = null;
  for (const resolved of weightsByJoint.values()) {
    if (!best || resolved.weight > best.weight) best = resolved;
  }

  return best
    ? { jointId: best.jointId, sourceBoneName: best.sourceBoneName }
    : null;
}

function resolveDominantJointForVertex({
  bones,
  itemSize,
  skeletonType,
  skinIndex,
  skinWeight,
  vertexIndex,
}: {
  bones: readonly Object3D[];
  itemSize: number;
  skeletonType: MotionEditorBonePickerSkeletonType;
  skinIndex: BufferAttribute;
  skinWeight: BufferAttribute;
  vertexIndex: number;
}) {
  const weightsByJoint = new Map<KeyPoseFkJointId, number>();

  for (let component = 0; component < itemSize; component += 1) {
    const weight = Number(
      bufferAttributeComponent(skinWeight, vertexIndex, component),
    );
    if (weight <= 1e-6) continue;

    const boneIndex = Math.round(
      Number(bufferAttributeComponent(skinIndex, vertexIndex, component)),
    );
    const jointId = resolvePoseMeshBoneJointId(bones[boneIndex], skeletonType);
    if (!jointId) continue;

    weightsByJoint.set(jointId, (weightsByJoint.get(jointId) ?? 0) + weight);
  }

  let best: { jointId: KeyPoseFkJointId; weight: number } | null = null;
  for (const [jointId, weight] of weightsByJoint.entries()) {
    if (!best || weight > best.weight) best = { jointId, weight };
  }

  return best;
}

function resolveDetailBoneName(
  rawBoneName: string,
  skeletonType: MotionEditorBonePickerSkeletonType,
): KeyPoseFkJointId | null {
  const boneName = normalizeBoneName(rawBoneName);
  if (!boneName) return null;
  if (
    skeletonType === 'mhr' &&
    (boneName === 'somadriverroot' || boneName === 'bodyworld')
  ) {
    return null;
  }

  if (skeletonType === 'mhr') {
    const mhrJointId = resolveMhrDetailBoneName(boneName);
    if (mhrJointId) return mhrJointId;
  }

  if (/left.*(hand|thumb|index|middle|ring|pinky)/.test(boneName)) {
    return 'left_wrist';
  }
  if (/right.*(hand|thumb|index|middle|ring|pinky)/.test(boneName)) {
    return 'right_wrist';
  }
  if (/left.*toe/.test(boneName)) return 'left_foot';
  if (/right.*toe/.test(boneName)) return 'right_foot';
  if (/(head.*end|jaw|eye)/.test(boneName)) return 'head';

  return null;
}

function resolveMhrDetailBoneName(boneName: string): KeyPoseFkJointId | null {
  if (/^l(?:upleg|thigh)twist\d+proc$/.test(boneName)) {
    return 'left_hip';
  }
  if (/^r(?:upleg|thigh)twist\d+proc$/.test(boneName)) {
    return 'right_hip';
  }
  if (/^l(?:lowleg|shin)twist\d+proc$/.test(boneName)) {
    return 'left_knee';
  }
  if (/^r(?:lowleg|shin)twist\d+proc$/.test(boneName)) {
    return 'right_knee';
  }
  if (/^l(?:subtalar|transversetarsal|ball)$/.test(boneName)) {
    return 'left_foot';
  }
  if (/^r(?:subtalar|transversetarsal|ball)$/.test(boneName)) {
    return 'right_foot';
  }
  if (/^luparmtwist\d+proc$/.test(boneName)) {
    return 'left_shoulder';
  }
  if (/^ruparmtwist\d+proc$/.test(boneName)) {
    return 'right_shoulder';
  }
  if (/^llowarmtwist\d+proc$/.test(boneName)) {
    return 'left_elbow';
  }
  if (/^rlowarmtwist\d+proc$/.test(boneName)) {
    return 'right_elbow';
  }
  if (
    /^l(?:wristtwist|(?:pinky|ring|middle|index|thumb)\d*(?:null)?)$/.test(
      boneName,
    )
  ) {
    return 'left_wrist';
  }
  if (
    /^r(?:wristtwist|(?:pinky|ring|middle|index|thumb)\d*(?:null)?)$/.test(
      boneName,
    )
  ) {
    return 'right_wrist';
  }
  if (/^cnecktwist\d+proc$/.test(boneName)) {
    return 'neck';
  }
  if (
    /^(?:c(?:headnull|jawnull|teeth|tongue\d*)|[lr]eyenull)$/.test(boneName)
  ) {
    return 'head';
  }

  return null;
}

function bufferAttributeComponent(
  attribute: BufferAttribute,
  index: number,
  component: number,
) {
  if (component === 0) return attribute.getX(index);
  if (component === 1) return attribute.getY(index);
  if (component === 2) return attribute.getZ(index);
  return attribute.getW(index);
}

function getUserDataString(object: Object3D, key: string) {
  const value = object.userData[key];

  return typeof value === 'string' ? value : null;
}

function normalizeBoneName(value: string) {
  return value.replace(/[^a-z0-9]/gi, '').toLowerCase();
}
